import { useEffect, useId, useState, type ReactNode } from 'react';
import {
  banderaDe,
  esPaisConocido,
  nombrePais,
  paisesOrdenados,
  partirTelefono,
  prefijoDe,
} from '@alexiarest/shared';

interface PhoneFieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  /** Numero completo en E.164 («+34600123456») o vacio. */
  value: string;
  onChange: (value: string) => void;
  /** Pais del restaurante: propone su prefijo mientras el usuario no elija otro. */
  defaultCountry?: string;
  disabled?: boolean;
}

const OPCIONES_PREFIJO = paisesOrdenados().map((p) => ({
  value: p.code,
  label: `${banderaDe(p.code)} ${p.dial} · ${nombrePais(p.code) ?? p.code}`,
}));

const soloCifras = (v: string): string => v.replace(/\D/g, '');

/**
 * Telefono en dos cajas: prefijo del pais y numero. Hacia fuera es un solo
 * valor en E.164, que es lo que guarda la API y lo que abre WhatsApp.
 *
 * Por que dos cajas y no una: con una sola, la mitad de los clientes de
 * America dejaba el numero sin prefijo y no habia forma de escribirles
 * (7 sep 2026). El prefijo sale ya puesto segun el pais del restaurante.
 */
export function PhoneField({
  label,
  hint,
  error,
  required,
  value,
  onChange,
  defaultCountry,
  disabled,
}: PhoneFieldProps): JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;
  const paisPorDefecto = esPaisConocido(defaultCountry) ? defaultCountry!.toUpperCase() : 'ES';

  const [inicial] = useState(() => partirTelefono(value, paisPorDefecto));
  const [pais, setPais] = useState(inicial.country ?? paisPorDefecto);
  const [nacional, setNacional] = useState(inicial.national);
  // El prefijo sigue al pais del restaurante hasta que el usuario lo toca o
  // hasta que el numero ya venia con uno.
  const [prefijoFijado, setPrefijoFijado] = useState(inicial.dial !== '');

  const emitir = (p: string, n: string) => {
    const cifras = soloCifras(n);
    onChange(cifras ? `${prefijoDe(p)}${cifras}` : '');
  };

  // Un numero guardado sin prefijo (dato antiguo) se completa con el del pais
  // nada mas pintarse: si no, el siguiente «Guardar» lo rechazaria.
  useEffect(() => {
    if (value && inicial.dial === '' && inicial.national) emitir(pais, inicial.national);
    // Solo al montar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (prefijoFijado) return;
    setPais((actual) => {
      if (actual === paisPorDefecto) return actual;
      if (soloCifras(nacional)) emitir(paisPorDefecto, nacional);
      return paisPorDefecto;
    });
    // Reacciona solo al cambio de pais del restaurante.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paisPorDefecto, prefijoFijado]);

  const onNumero = (raw: string) => {
    // Pegar «+39 333 123 4567» entero en la caja del numero tambien vale.
    if (raw.trim().startsWith('+')) {
      const partes = partirTelefono(raw, pais);
      if (partes.country) {
        setPais(partes.country);
        setPrefijoFijado(true);
        setNacional(partes.national);
        emitir(partes.country, partes.national);
        return;
      }
    }
    const limpio = raw.replace(/[^\d\s]/g, '');
    setNacional(limpio);
    emitir(pais, limpio);
  };

  return (
    <div className={`field ${error ? 'field--error' : ''}`.trim()}>
      <label className="field__label" htmlFor={id}>
        {label}
        {required && <span className="field__required" aria-hidden="true"> *</span>}
      </label>
      <div className="telefono">
        <select
          className="input telefono__prefijo"
          aria-label="Prefijo del país"
          value={pais}
          disabled={disabled}
          onChange={(event) => {
            setPais(event.target.value);
            setPrefijoFijado(true);
            emitir(event.target.value, nacional);
          }}
        >
          {OPCIONES_PREFIJO.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <input
          id={id}
          className="input"
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={nacional}
          onChange={(event) => onNumero(event.target.value)}
          placeholder="600 000 000"
          required={required}
          disabled={disabled}
          aria-describedby={hint || error ? hintId : undefined}
          aria-invalid={error ? true : undefined}
        />
      </div>
      {(error || hint) && (
        <p className="field__hint" id={hintId}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
}
